import { useState, useEffect, useRef } from "react";
import { useAppSelector } from "../../app/hooks";
import { Assets, BLEND_MODES, TextStyle } from "pixi.js";
import { AnimatedSprite, Container, Sprite, Text } from "@pixi/react";

/* Remove above for production */
function Pool(props) {
  const width = 2000;
  const height = 1125;
  const [scale, setScale] = useState(
    window.innerWidth / window.innerHeight < width / height
      ? window.innerWidth / width
      : window.innerHeight / height
  );

  useEffect(() => {
    const handleWindowResize = () => {
      setScale(
        window.innerWidth / window.innerHeight < width / height
          ? window.innerWidth / width
          : window.innerHeight / height
      );
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [setScale]);

  // get the sheet json data, required for resolving animations
  const animations = Assets.cache.get("sprites").data.animations;

  const appState = useAppSelector((state) => state.app);

  const [fireAlpha, setFireAlpha] = useState(appState.fireBowl ? 1 : 0);
  const [waterfallAlpha, setWaterfallAlpha] = useState(
    appState.waterfall ? 1 : 0
  );
  const [spilloverAlpha, setSpilloverAlpha] = useState(
    !appState.spillover ? 1 : 0
  );
  const [bubblerAlpha, setBubblerAlpha] = useState(!appState.bubblers ? 1 : 0);
  const [spaJetsAlpha, setSpaJetsAlpha] = useState(!appState.spaJets ? 1 : 0);
  const [poolTempVisible, setPoolTempVisible] = useState(false);
  const [spaTempVisible, setSpaTempVisible] = useState(false);
  const [poolTempAlpha, setPoolTempAlpha] = useState(0);
  const [spaTempAlpha, setSpaTempAlpha] = useState(0);

  // Use useRef for mutable variables that we want to persist
  // without triggering a re-render on their change
  const requestRef = useRef();
  const previousTimeRef = useRef();
  const appStateRef = useRef();
  const poolTempVisibleRef = useRef();
  const spaTempVisibleRef = useRef();
  const poolTempTimerRef = useRef();
  const spaTempTimerRef = useRef();
  const prevSpaTempRef = useRef();
  const prevPoolTempRef = useRef();

  const animate = (time) => {
    if (previousTimeRef.current !== undefined) {
      if (
        prevPoolTempRef.current &&
        prevPoolTempRef.current !== appStateRef.current.poolTemp
      ) {
        setPoolTempVisible(true);
        if (poolTempTimerRef.current) {
          clearTimeout(poolTempTimerRef.current);
        }
        poolTempTimerRef.current = setTimeout(() => {
          setPoolTempVisible(false);
          poolTempTimerRef.current = null;
        }, 3000);
      }
      if (
        prevSpaTempRef.current &&
        prevSpaTempRef.current !== appStateRef.current.spaTemp
      ) {
        setSpaTempVisible(true);
        if (spaTempTimerRef.current) {
          clearTimeout(spaTempTimerRef.current);
        }
        spaTempTimerRef.current = setTimeout(() => {
          setSpaTempVisible(false);
          spaTempTimerRef.current = null;
        }, 3000);
      }
      prevPoolTempRef.current = appStateRef.current.poolTemp;
      prevSpaTempRef.current = appStateRef.current.spaTemp;
      // const deltaTime = time - previousTimeRef.current;
      // Pass on a function to the setter of the state
      // to make sure we always have the latest state
      setFireAlpha((prevAlpha) => {
        if (appStateRef.current.fireBowl && prevAlpha < 1) {
          return Math.min(1, prevAlpha + 0.05);
        } else if (!appStateRef.current.fireBowl && prevAlpha > 0) {
          return Math.max(0, prevAlpha - 0.05);
        }
        return prevAlpha;
      });
      setWaterfallAlpha((prevAlpha) => {
        if (appStateRef.current.waterfall && prevAlpha < 1) {
          return Math.min(1, prevAlpha + 0.05);
        } else if (!appStateRef.current.waterfall && prevAlpha > 0) {
          return Math.max(0, prevAlpha - 0.05);
        }
        return prevAlpha;
      });
      setSpilloverAlpha((prevAlpha) => {
        if (!appStateRef.current.spillover && prevAlpha < 1) {
          return Math.min(1, prevAlpha + 0.05);
        } else if (appStateRef.current.spillover && prevAlpha > 0) {
          return Math.max(0, prevAlpha - 0.05);
        }
        return prevAlpha;
      });
      setBubblerAlpha((prevAlpha) => {
        if (appStateRef.current.bubblers && prevAlpha < 1) {
          return Math.min(1, prevAlpha + 0.05);
        } else if (!appStateRef.current.bubbler && prevAlpha > 0) {
          return Math.max(0, prevAlpha - 0.05);
        }
        return prevAlpha;
      });
      setSpaJetsAlpha((prevAlpha) => {
        if (appStateRef.current.spaJets && prevAlpha < 1) {
          return Math.min(1, prevAlpha + 0.05);
        } else if (!appStateRef.current.spaJets && prevAlpha > 0) {
          return Math.max(0, prevAlpha - 0.05);
        }
        return prevAlpha;
      });
      setPoolTempAlpha((prevAlpha) => {
        if (poolTempVisibleRef.current && prevAlpha < 1) {
          return Math.min(1, prevAlpha + 0.05);
        } else if (!poolTempVisibleRef.current && prevAlpha > 0) {
          return Math.max(0, prevAlpha - 0.05);
        }
        return prevAlpha;
      });
      setSpaTempAlpha((prevAlpha) => {
        if (spaTempVisibleRef.current && prevAlpha < 1) {
          return Math.min(1, prevAlpha + 0.05);
        } else if (!spaTempVisibleRef.current && prevAlpha > 0) {
          return Math.max(0, prevAlpha - 0.05);
        }
        return prevAlpha;
      });
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    appStateRef.current = appState;
    poolTempVisibleRef.current = poolTempVisible;
    spaTempVisibleRef.current = spaTempVisible;
    return () => cancelAnimationFrame(requestRef.current);
  }, [appState, poolTempVisible, spaTempVisible]); // Make sure the effect runs only once

  return (
    <Container scale={scale}>
      <Sprite
        image={"background.jpg"}
        x={width * 0.5}
        y={height * 0.5}
        anchor={{ x: 0.5, y: 0.5 }}
      />
      <AnimatedSprite
        animationSpeed={0.5}
        images={animations["water-movement"]}
        isPlaying={true}
        initialFrame={0}
        x={width * 0.601}
        y={height * 0.6}
        anchor={{ x: 0.5, y: 0.5 }}
      />
      {appState.poolLights && appState.poolLightsColor !== "#FFFFFF" ? (
        <Sprite
          image={"pool-light-mask.png"}
          blendMode={BLEND_MODES.SCREEN}
          tint={parseInt(appState.poolLightsColor.replace("#", ""), 16)}
          alpha={appState.poolLightsBrightness / 100}
          x={width * 0.601}
          y={height * 0.564}
          anchor={{ x: 0.5, y: 0.5 }}
        />
      ) : undefined}
      {appState.poolLights && appState.poolLightsColor === "#FFFFFF" ? (
        <Sprite
          image={"pool-light-mask-white.png"}
          blendMode={BLEND_MODES.SCREEN}
          tint={parseInt(appState.poolLightsColor.replace("#", ""), 16)}
          alpha={appState.poolLightsBrightness / 100}
          x={width * 0.601}
          y={height * 0.564}
          anchor={{ x: 0.5, y: 0.5 }}
        />
      ) : undefined}
      <AnimatedSprite
        animationSpeed={0.5}
        images={animations["spa-jets"]}
        isPlaying={true}
        initialFrame={0}
        x={width * 0.604}
        y={height * 0.698}
        anchor={{ x: 0.5, y: 0.5 }}
        alpha={spaJetsAlpha}
      />
      {appState.spaLights && appState.spaLightsColor !== "#FFFFFF" ? (
        <Sprite
          image={"spa-light-mask.png"}
          blendMode={BLEND_MODES.SCREEN}
          tint={parseInt(appState.spaLightsColor.replace("#", ""), 16)}
          alpha={appState.spaLightsBrightness / 100}
          x={width * 0.604}
          y={height * 0.698}
          anchor={{ x: 0.5, y: 0.5 }}
        />
      ) : undefined}
      {appState.spaLights && appState.spaLightsColor === "#FFFFFF" ? (
        <Sprite
          image={"spa-light-mask-white.png"}
          blendMode={BLEND_MODES.SCREEN}
          tint={parseInt(appState.spaLightsColor.replace("#", ""), 16)}
          alpha={appState.spaLightsBrightness / 100}
          x={width * 0.604}
          y={height * 0.698}
          anchor={{ x: 0.5, y: 0.5 }}
        />
      ) : undefined}
      {appState.bubblers && appState.bubblerLights ? (
        <Container>
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["bubblers"]}
            blendMode={BLEND_MODES.SCREEN}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.6025}
            y={height * 0.47}
            anchor={{ x: 0.5, y: 0.5 }}
            alpha={bubblerAlpha}
          />
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["bubbler-light-mask"]}
            blendMode={BLEND_MODES.ADD}
            tint={parseInt(appState.bubblerLightsColor.replace("#", ""), 16)}
            alpha={appState.bubblerLightsBrightness / 100}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.6025}
            y={height * 0.47}
            anchor={{ x: 0.5, y: 0.5 }}
          />
        </Container>
      ) : (
        <AnimatedSprite
          animationSpeed={0.5}
          images={animations["bubblers"]}
          blendMode={BLEND_MODES.SCREEN}
          isPlaying={true}
          initialFrame={0}
          x={width * 0.6025}
          y={height * 0.47}
          anchor={{ x: 0.5, y: 0.5 }}
          alpha={bubblerAlpha}
        />
      )}

      {appState.waterfall && appState.waterfallLights ? (
        <Container>
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["waterfalls"]}
            blendMode={BLEND_MODES.SCREEN}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.889}
            y={height * 0.564}
            anchor={{ x: 0.5, y: 0.5 }}
            alpha={waterfallAlpha}
          />
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["waterfalls"]}
            blendMode={BLEND_MODES.SCREEN}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.313}
            y={height * 0.564}
            anchor={{ x: 0.5, y: 0.5 }}
            scale={{ x: -1, y: 1 }}
            alpha={waterfallAlpha}
          />
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["waterfall-light-mask"]}
            blendMode={BLEND_MODES.ADD}
            tint={parseInt(appState.waterfallLightsColor.replace("#", ""), 16)}
            alpha={appState.waterfallLightsBrightness / 100}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.889}
            y={height * 0.564}
            anchor={{ x: 0.5, y: 0.5 }}
          />
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["waterfall-light-mask"]}
            blendMode={BLEND_MODES.ADD}
            tint={parseInt(appState.waterfallLightsColor.replace("#", ""), 16)}
            alpha={appState.waterfallLightsBrightness / 100}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.313}
            y={height * 0.564}
            anchor={{ x: 0.5, y: 0.5 }}
            scale={{ x: -1, y: 1 }}
          />
        </Container>
      ) : (
        <Container>
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["waterfalls"]}
            blendMode={BLEND_MODES.SCREEN}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.889}
            y={height * 0.564}
            anchor={{ x: 0.5, y: 0.5 }}
            alpha={waterfallAlpha}
          />
          <AnimatedSprite
            animationSpeed={0.5}
            images={animations["waterfalls"]}
            blendMode={BLEND_MODES.SCREEN}
            isPlaying={true}
            initialFrame={0}
            x={width * 0.313}
            y={height * 0.564}
            anchor={{ x: 0.5, y: 0.5 }}
            scale={{ x: -1, y: 1 }}
            alpha={waterfallAlpha}
          />
        </Container>
      )}
      <AnimatedSprite
        animationSpeed={0.5}
        images={animations["fire-back"]}
        blendMode={BLEND_MODES.SCREEN}
        isPlaying={true}
        initialFrame={0}
        x={width * 0.335}
        y={height * 0.385}
        anchor={{ x: 0, y: 0 }}
        scale={{ x: -1, y: 1 }}
        alpha={fireAlpha}
      />
      <AnimatedSprite
        animationSpeed={0.5}
        images={animations["fire-back"]}
        blendMode={BLEND_MODES.SCREEN}
        isPlaying={true}
        initialFrame={0}
        x={width * 0.896}
        y={height * 0.385}
        anchor={{ x: 0, y: 0 }}
        scale={{ x: -1, y: 1 }}
        alpha={fireAlpha}
      />
      <AnimatedSprite
        animationSpeed={0.5}
        images={animations["fire-front"]}
        blendMode={BLEND_MODES.SCREEN}
        isPlaying={true}
        initialFrame={0}
        x={width * 0.255}
        y={height * 0.505}
        anchor={{ x: 0, y: 0 }}
        scale={{ x: -1, y: 1 }}
        alpha={fireAlpha}
      />
      <AnimatedSprite
        animationSpeed={0.5}
        images={animations["fire-front"]}
        blendMode={BLEND_MODES.SCREEN}
        isPlaying={true}
        initialFrame={0}
        x={width * 0.977}
        y={height * 0.505}
        anchor={{ x: 0, y: 0 }}
        scale={{ x: -1, y: 1 }}
        alpha={fireAlpha}
      />
      <Sprite
        image={"dry-spillover.png"}
        x={width * 0.602}
        y={height * 0.599}
        anchor={{ x: 0.5, y: 0.5 }}
        alpha={spilloverAlpha}
      />
      <Container alpha={poolTempAlpha}>
        <Sprite
          image={"cool-icon.png"}
          x={width * 0.602}
          y={height * 0.55}
          scale={0.5}
          anchor={{ x: 0.5, y: 0.5 }}
        />
        <Text
          text={appState.poolTemp + "°"}
          style={
            new TextStyle({
              align: "center",
              fontFamily: '"Source Sans Pro", Helvetica, sans-serif',
              fontSize: 50,
              fontWeight: 700,
              fill: "#ffffff",
              letterSpacing: 5,
            })
          }
          x={width * 0.6}
          y={height * 0.53}
          anchor={{ x: 0.5, y: 0.5 }}
        />
      </Container>
      <Container alpha={spaTempAlpha}>
        <Sprite
          image={"heat-icon.png"}
          x={width * 0.602}
          y={height * 0.7}
          scale={0.5}
          anchor={{ x: 0.5, y: 0.5 }}
        />
        <Text
          text={appState.spaTemp + "°"}
          style={
            new TextStyle({
              align: "center",
              fontFamily: '"Source Sans Pro", Helvetica, sans-serif',
              fontSize: 50,
              fontWeight: 700,
              fill: "#ffffff",
              letterSpacing: 5,
            })
          }
          x={width * 0.6}
          y={height * 0.675}
          anchor={{ x: 0.5, y: 0.5 }}
        />
      </Container>
      <Sprite
        image={"info-icon.png"}
        x={width * 0.96}
        y={height * 0.95}
        scale={0.4}
        anchor={{ x: 0.5, y: 0.5 }}
        interactive={true}
        onclick={() => {
          alert(`1. Jandy low-voltage nicheless LED underwater pool and spa lights are certified for “wet-area” installations, are completely nonmetallic and intended to be powered by a NRTL-Listed Low-Voltage Pool/Spa transformer listed to UL-379. When properly installed these luminaires have been found to be acceptable for installation in areas within 10 feet (3m) of the pool or spa.

2. Installation must comply with all applicable local codes and requirements from Authorities Having Jurisdiction.`);
        }}
      />
    </Container>
  );
}

export default Pool;
